import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, UserRole } from '../contexts/AuthContext';
import ParentDashboard from './ParentDashboard';

// Component to render based on user role
const RoleBasedDashboard: React.FC<{ role: UserRole }> = ({ role }) => {
  const { t } = useTranslation();
  
  // For parent users, render the ParentDashboard component
  if (role === 'parent') {
    return <ParentDashboard />;
  }
  
  switch (role) {
    case 'admin':
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4">{t('dashboard.welcome')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DashboardCard 
              title={t('roles.student')} 
              count={254} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('roles.teacher')} 
              count={42} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('content.title')} 
              count={189} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              } 
            />
          </div>
          
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">{t('dashboard.recentActivity')}</h3>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <ul className="divide-y divide-gray-200">
                <ActivityItem 
                  user="János Kovács" 
                  action="uploaded a new lesson" 
                  target="Mathematics Grade 3"
                  time="2 hours ago"
                />
                <ActivityItem 
                  user="Katalin Nagy" 
                  action="created a new account" 
                  target=""
                  time="5 hours ago"
                />
                <ActivityItem 
                  user="Bálint Szabó" 
                  action="edited" 
                  target="English Grammar Test"
                  time="Yesterday"
                />
                <ActivityItem 
                  user="Zoltán Kiss" 
                  action="assigned homework to" 
                  target="Class 4B"
                  time="2 days ago"
                />
              </ul>
            </div>
          </div>
        </div>
      );
    
    case 'teacher':
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4">{t('dashboard.welcome')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DashboardCard 
              title={t('students.class')} 
              count={5} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('students.management')} 
              count={124} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('content.management')} 
              count={36} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              } 
            />
          </div>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-4">{t('dashboard.upcomingClasses')}</h3>
              <div className="bg-white rounded-lg shadow p-4">
                <ul className="divide-y divide-gray-200">
                  <li className="py-3 flex justify-between">
                    <div>
                      <p className="font-medium">3A osztály - Matematika</p>
                      <p className="text-sm text-gray-500">Szorzás és osztás</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#309D97]">Ma</p>
                      <p className="text-sm text-gray-500">11:00</p>
                    </div>
                  </li>
                  <li className="py-3 flex justify-between">
                    <div>
                      <p className="font-medium">4B osztály - Természetismeret</p>
                      <p className="text-sm text-gray-500">Növények életciklusa</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#309D97]">Holnap</p>
                      <p className="text-sm text-gray-500">9:30</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4">{t('dashboard.studentActivities')}</h3>
              <div className="bg-white rounded-lg shadow p-4">
                <ul className="divide-y divide-gray-200">
                  <li className="py-3">
                    <p className="font-medium">Nagy Péter</p>
                    <p className="text-sm text-gray-600">Matematika kvíz kitöltve, eredménye: 85%</p>
                    <p className="text-xs text-gray-500 mt-1">1 órája</p>
                  </li>
                  <li className="py-3">
                    <p className="font-medium">Kovács Anna</p>
                    <p className="text-sm text-gray-600">Megkezdte a Természetismeret leckét az Elektromosságról</p>
                    <p className="text-xs text-gray-500 mt-1">3 órája</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    
    case 'student':
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4">{t('dashboard.welcome')}</h2>
          <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border-l-4 border-[#309D97]">
            <p className="text-sm text-gray-500">Ez az admin panel nézet a tanulói fiókokhoz.</p>
            <p className="text-sm text-gray-500 mt-2">A tanulók általában a Sulibari mobilalkalmazást használják, de ezt a panelt is használhatják a fiókkezeléshez.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DashboardCard 
              title={t('content.subject')} 
              count={6} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('dashboard.completedTasks')} 
              count={42} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              } 
            />
            <DashboardCard 
              title={t('dashboard.achievementPoints')} 
              count={350} 
              icon={
                <svg className="w-12 h-12 text-[#309D97]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              } 
            />
          </div>
          
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">{t('dashboard.myProgress')}</h3>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Matematika</span>
                    <span className="text-sm text-gray-500">75%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#309D97] h-2 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Természetismeret</span>
                    <span className="text-sm text-gray-500">62%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#309D97] h-2 rounded-full" style={{ width: '62%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Magyar</span>
                    <span className="text-sm text-gray-500">90%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#309D97] h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
      
    default:
      return <div>{t('errors.unknown')}</div>;
  }
};

// Dashboard card component
interface DashboardCardProps {
  title: string;
  count: number;
  icon: React.ReactNode;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, count, icon }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">{icon}</div>
        <div className="ml-5">
          <p className="text-gray-500">{title}</p>
          <h3 className="text-3xl font-bold">{count}</h3>
        </div>
      </div>
    </div>
  );
};

// Activity item component
interface ActivityItemProps {
  user: string;
  action: string;
  target: string;
  time: string;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ user, action, target, time }) => {
  return (
    <li className="p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3">
          <div className="w-8 h-8 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97]">
            {user.charAt(0)}
          </div>
        </div>
        <div>
          <p className="text-sm">
            <span className="font-medium">{user}</span> {action}
            {target && <span className="font-medium"> {target}</span>}
          </p>
          <p className="text-xs text-gray-500 mt-1">{time}</p>
        </div>
      </div>
    </li>
  );
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();

  if (!userData) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('common.dashboard')}</h1>
      <RoleBasedDashboard role={userData.role} />
    </div>
  );
};

export default Dashboard; 